/**
 * Les modèles concurrents (SPEC.md §6).
 *
 * Chacun prédit la même grandeur observable — la longueur de l'ombre d'un
 * gnomon vertical — à partir des mêmes entrées. Ce qui les sépare n'est pas la
 * qualité de l'accord, c'est le **nombre d'hypothèses libres** qu'il faut leur
 * concéder pour l'obtenir. C'est cette monnaie-là que le comparateur affiche.
 *
 * ## Pourquoi le modèle plat ajusté n'est pas calculé
 *
 * Le modèle plat peut suivre n'importe quelle série de mesures si on lui
 * accorde une hauteur de Soleil par date. Calculer ces hauteurs reviendrait à
 * « chercher les paramètres qui font coller un modèle aux données » — ce que
 * SPEC §3.5 et CLAUDE.md §4 interdisent sans exception.
 *
 * Ce n'est pas une limitation qu'on subit, c'est la démonstration. Le logiciel
 * annonce le coût du sauvetage — une hypothèse libre par point — et laisse
 * l'utilisateur en tirer la conclusion. Un modèle qui a besoin d'autant de
 * paramètres que de mesures n'explique rien : il les recopie.
 */

import { coreFr } from "../i18n/core-fr.js";
import { PI, cos, sqrt, tan } from "./math.js";
import { solarDeclination, solarElevationAt } from "./solar.js";
import { hourAngle } from "./solar.js";
import type { Quantity } from "./units.js";
import { dimensionless, quantity, valueIn } from "./units.js";

export interface ShadowInput {
  readonly instant: Quantity<"time">;
  readonly latitude: Quantity<"angle">;
  readonly longitude: Quantity<"angle">;
  readonly gnomonHeight: Quantity<"length">;
}

/**
 * Un modèle a le droit de n'avoir aucune prédiction — le Soleil sous
 * l'horizon n'est pas une erreur, c'est une absence d'ombre. On ne renvoie
 * jamais une valeur inventée pour combler le trou.
 */
export type Prediction =
  | { readonly kind: "shadow"; readonly length: Quantity<"length"> }
  | { readonly kind: "none"; readonly reason: string };

/**
 * Un compte d'hypothèses libres n'est pas toujours un nombre : « deux, quel
 * que soit le nombre de relevés » et « une par relevé » sont deux natures
 * différentes, et c'est précisément leur différence que l'expérience met en
 * scène. Les confondre dans un entier ferait perdre le propos.
 */
export type FreeHypotheses =
  | { readonly kind: "fixed"; readonly count: Quantity<"dimensionless"> }
  | { readonly kind: "perObservation" };

export interface ModelDescriptor<Id extends string = string> {
  readonly id: Id;
  readonly label: string;
  readonly freeHypotheses: FreeHypotheses;
  readonly assumptions: readonly string[];
}

/** Résout le compte pour un jeu de relevés donné. */
export function countFreeHypotheses(
  model: ModelDescriptor,
  observationCount: Quantity<"dimensionless">,
): Quantity<"dimensionless"> {
  return model.freeHypotheses.kind === "fixed"
    ? model.freeHypotheses.count
    : dimensionless(valueIn(observationCount, "1"));
}

const DEG_TO_RAD = PI / 180;

/**
 * `L = h / tan a` — géométrie pure, exacte.
 *
 * Commune aux deux modèles : ils ne diffèrent que par l'élévation qu'ils
 * prédisent, jamais par la façon dont une ombre se projette.
 */
export function shadowLength(
  gnomonHeight: Quantity<"length">,
  elevation: Quantity<"angle">,
): Prediction {
  const degrees = valueIn(elevation, "deg");
  if (degrees <= 0) {
    return {
      kind: "none",
      reason: coreFr.models.sunBelowHorizon,
    };
  }
  const height = valueIn(gnomonHeight, "m");
  return { kind: "shadow", length: quantity(height / tan(degrees * DEG_TO_RAD), "m") };
}

// ─────────────────────────────────────────────────────────────────────────────
// Modèle A — Terre sphérique, Soleil lointain
// ─────────────────────────────────────────────────────────────────────────────

export const SPHERICAL_MODEL: ModelDescriptor = {
  id: "spherical",
  label: coreFr.models.sphericalLabel,
  freeHypotheses: { kind: "fixed", count: dimensionless(0) },
  assumptions: [
    coreFr.models.sphericalEarth,
    coreFr.models.parallelSunRays,
    coreFr.models.axialTilt,
  ],
};

/**
 * Aucune hypothèse libre : latitude et instant viennent du dataset,
 * déclinaison et angle horaire de l'éphéméride — laquelle est une **référence
 * externe**, pas une mesure d'arbitrage (SPEC §3.2).
 */
export function predictSphericalShadow(input: ShadowInput): Prediction {
  const elevation = solarElevationAt(input.instant, input.latitude, input.longitude);
  return shadowLength(input.gnomonHeight, elevation);
}

// ─────────────────────────────────────────────────────────────────────────────
// Modèle B structuré — plan horizontal, Soleil local à hauteur fixe
// ─────────────────────────────────────────────────────────────────────────────

export interface StructuredFlatParameters {
  /** Hauteur du Soleil au-dessus du plan. Première hypothèse libre. */
  readonly sunHeight: Quantity<"length">;
  /** Échelle de la carte plate, en distance par degré. Seconde hypothèse libre. */
  readonly metresPerDegree: Quantity<"length">;
}

export const STRUCTURED_FLAT_MODEL: ModelDescriptor = {
  id: "flat-structured",
  label: coreFr.models.flatFixedLabel,
  freeHypotheses: { kind: "fixed", count: dimensionless(2) },
  assumptions: [
    coreFr.models.flatEarth,
    coreFr.models.fixedSunHeight,
    coreFr.models.annualSolarCycle,
    coreFr.models.latitudeDistance,
  ],
};

/**
 * Géométrie de la carte plate usuelle : pôle au centre, distance au centre
 * proportionnelle à la colatitude. La distance entre l'observateur et le point
 * subsolaire suit alors la loi des cosinus dans le plan, l'angle au centre
 * étant l'angle horaire.
 *
 * À midi solaire elle se réduit à `d = k · |φ − δ|`, d'où `L = h · k · z / Hs`
 * — une droite, là où le modèle sphérique donne `L = h · tan z`. C'est
 * exactement là que les deux se séparent.
 */
export function predictStructuredFlatShadow(
  input: ShadowInput,
  parameters: StructuredFlatParameters,
): Prediction {
  const scale = valueIn(parameters.metresPerDegree, "m");
  const declination = valueIn(solarDeclination(input.instant), "deg");
  const observerRadius = scale * (90 - valueIn(input.latitude, "deg"));
  const subsolarRadius = scale * (90 - declination);
  const centralAngle = valueIn(hourAngle(input.instant, input.longitude), "deg");

  const distance = sqrt(
    observerRadius * observerRadius +
      subsolarRadius * subsolarRadius -
      2 * observerRadius * subsolarRadius * cos(centralAngle * DEG_TO_RAD),
  );

  const sunHeight = valueIn(parameters.sunHeight, "m");
  if (sunHeight <= 0) {
    return {
      kind: "none",
      reason: coreFr.models.positiveSunHeightRequired,
    };
  }

  const height = valueIn(input.gnomonHeight, "m");
  return { kind: "shadow", length: quantity((height * distance) / sunHeight, "m") };
}

// ─────────────────────────────────────────────────────────────────────────────
// Modèle B ajusté — annoncé, compté, jamais résolu
// ─────────────────────────────────────────────────────────────────────────────

export const ADJUSTED_FLAT_MODEL: ModelDescriptor = {
  id: "flat-adjusted",
  label: coreFr.models.flatTunedLabel,
  freeHypotheses: { kind: "perObservation" },
  assumptions: [
    coreFr.models.flatEarth,
    coreFr.models.independentSunHeights,
    coreFr.models.oneParameterPerMeasurement,
  ],
};

/**
 * Le coût du sauvetage : une hypothèse libre par relevé.
 *
 * Le logiciel s'arrête là. Calculer les hauteurs qui font coller le modèle
 * serait résoudre un problème inverse, ce qu'il ne fait jamais (CLAUDE.md §4).
 */
export function freeHypothesesOfAdjustedFlat(
  observationCount: Quantity<"dimensionless">,
): Quantity<"dimensionless"> {
  return countFreeHypotheses(ADJUSTED_FLAT_MODEL, observationCount);
}
